import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Clock, 
  ExternalLink, 
  Mail, 
  CheckCircle, 
  RefreshCw,
  FileText,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const FormularioPendiente = () => {
  const { user, API, logout } = useContext(AuthContext);
  const [formularioConfig, setFormularioConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadFormularioConfig();
  }, []);

  const loadFormularioConfig = async () => {
    try {
      const response = await fetch(`${API}/formulario-config`);
      if (response.ok) {
        const data = await response.json();
        setFormularioConfig(data);
      }
    } catch (error) {
      console.error('Error loading formulario config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    setRefreshing(true);
    try {
      // Check current user status
      const response = await fetch(`${API}/users/me`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.estado_formulario === 'aprobado') {
          toast.success('¡Tu cuenta ha sido aprobada! Redirigiendo...');
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          toast.info('Tu cuenta aún está pendiente de aprobación');
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
      toast.error('Error al verificar estado');
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-4"></div>
          <div className="h-40 bg-gray-200 rounded w-96"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Cuenta Pendiente de Aprobación
          </h1>
          <p className="text-gray-600">
            Hola <strong>{user?.nombre}</strong>, tu registro está casi completo
          </p>
        </div>

        {/* Main Card */}
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-4">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                <Clock className="h-3 w-3 mr-1" />
                Estado Pendiente
              </Badge>
            </div>
            <CardTitle className="text-xl text-gray-800">
              {formularioConfig?.titulo || 'Formulario de Información de Estudiante'}
            </CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="text-center space-y-3">
              <p className="text-gray-600">
                {formularioConfig?.descripcion || 'Para completar tu registro, necesitas llenar el formulario de información de estudiante.'}
              </p>
              
              {formularioConfig ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-left">
                      <h4 className="font-semibold text-blue-800 mb-1">Siguiente paso:</h4>
                      <p className="text-sm text-blue-700 mb-3">
                        Haz clic en el botón de abajo para acceder al formulario de Google. Una vez completado, 
                        un administrador revisará tu información y aprobará tu cuenta.
                      </p>
                      <Button 
                        onClick={() => window.open(formularioConfig.link_formulario, '_blank')}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Completar Formulario
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div className="text-left">
                      <h4 className="font-semibold text-amber-800 mb-1">Formulario no configurado</h4>
                      <p className="text-sm text-amber-700">
                        El formulario aún no ha sido configurado por los administradores. 
                        Por favor contacta al instituto para más información.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Manual del Estudiante */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start space-x-3">
                <div className="bg-blue-100 rounded-full p-2 flex-shrink-0">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-1">📘 Manual del Estudiante 2026</h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Mientras esperas la aprobación, te recomendamos descargar el Manual del Estudiante. 
                    Contiene toda la información que necesitas para usar la plataforma: clases, tareas, 
                    calificaciones, chat, pagos y más.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => window.open(`${API}/public/manual-estudiante`, '_blank')}
                      className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
                      size="sm"
                    >
                      <i className="fas fa-download mr-2"></i>
                      Descargar Manual (PDF)
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const pdfUrl = `${API}/public/manual-estudiante`;
                        window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`, '_blank');
                      }}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50 text-sm"
                      size="sm"
                    >
                      <i className="fas fa-eye mr-2"></i>
                      Ver Online
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Carta de Bienvenida - Ahora se envía por email */}
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="flex items-start space-x-3">
                <div className="bg-purple-100 rounded-full p-2 flex-shrink-0">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-purple-900 mb-1">📧 Carta de Bienvenida</h4>
                  <p className="text-sm text-purple-700">
                    La Carta de Bienvenida fue enviada a tu correo electrónico cuando tu cuenta fue aprobada. 
                    Por favor revisa tu bandeja de entrada (y también la carpeta de SPAM).
                  </p>
                  <p className="text-xs text-purple-600 mt-2">
                    ✉️ Enviada a: <strong>{user?.email}</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Videos de YouTube */}
            {formularioConfig?.videos_youtube && formularioConfig.videos_youtube.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fas fa-video text-red-600"></i>
                  <h4 className="font-semibold text-gray-800">Videos Instructivos</h4>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  {formularioConfig.videos_youtube.map((videoUrl, index) => {
                    // Convertir URL de YouTube o Vimeo a formato embed
                    const getEmbedUrl = (url) => {
                      try {
                        // YouTube
                        if (url.includes('youtu.be/')) {
                          const videoId = url.split('youtu.be/')[1].split('?')[0];
                          return `https://www.youtube.com/embed/${videoId}`;
                        } else if (url.includes('youtube.com/watch?v=')) {
                          const videoId = url.split('v=')[1].split('&')[0];
                          return `https://www.youtube.com/embed/${videoId}`;
                        } else if (url.includes('youtube.com/embed/')) {
                          return url;
                        }
                        // Vimeo
                        else if (url.includes('vimeo.com/')) {
                          const vimeoId = url.split('vimeo.com/')[1].split('?')[0].split('/')[0];
                          return `https://player.vimeo.com/video/${vimeoId}`;
                        } else if (url.includes('player.vimeo.com/')) {
                          return url;
                        }
                        return url;
                      } catch {
                        return url;
                      }
                    };

                    return (
                      <div key={index} className="bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                        <div className="aspect-video">
                          <iframe
                            width="100%"
                            height="100%"
                            src={getEmbedUrl(videoUrl)}
                            title={`Video instructivo ${index + 1}`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          ></iframe>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                ¿Necesitas ayuda?
              </h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Si tienes alguna pregunta sobre el formulario, puedes contactar a:</p>
                <p><strong>Email:</strong> admin@instituto.uy</p>
                <p><strong>Instituto Bíblico Apostólico del Uruguay</strong></p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={handleRefreshStatus}
                disabled={refreshing}
                className="flex-1"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Verificando...' : 'Verificar Estado'}
              </Button>
              
              <Button 
                variant="ghost"
                onClick={handleLogout}
                className="flex-1 text-gray-600"
              >
                Cerrar Sesión
              </Button>
            </div>

            {/* Status indicator */}
            <div className="text-center">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                Esperando aprobación del administrador
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>Una vez aprobado, tendrás acceso completo al sistema del instituto</p>
        </div>
      </div>
    </div>
  );
};

export default FormularioPendiente;